import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, Clock, CreditCard, X } from 'lucide-react';

interface TrialConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading?: boolean;
}

export function TrialConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: TrialConfirmationModalProps) {
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Start your 14-day free trial
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Get full access to Decision Sales OS and transform your sales process.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Benefits list */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-1 rounded-full bg-green-500/10">
                <Check className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="font-medium">14 days free access</p>
                <p className="text-sm text-muted-foreground">
                  Full access to all features during your trial
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-1 rounded-full bg-blue-500/10">
                <CreditCard className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="font-medium">No payment required now</p>
                <p className="text-sm text-muted-foreground">
                  You won't be charged during your trial period
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-1 rounded-full bg-orange-500/10">
                <X className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <p className="font-medium">Cancel anytime</p>
                <p className="text-sm text-muted-foreground">
                  No commitment, cancel before trial ends
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-1 rounded-full bg-amber-500/10">
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <div>
                <p className="font-medium">Access ends after trial</p>
                <p className="text-sm text-muted-foreground">
                  Access will be blocked at the end of the trial unless you upgrade
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation checkbox */}
          <div className="pt-4 border-t">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm">
                I understand this is a 14-day free trial and access will end if I don't upgrade
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="hero"
            onClick={handleConfirm}
            disabled={!confirmed || loading}
            className="flex-1"
          >
            {loading ? 'Creating account...' : 'Start 14-day free trial'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
